/**
 * forecast-rules.ts — Canon 5-bucket forecast taxonomy.
 *
 * SOURCE OF TRUTH: CANON.md lines 167-186 (locked 2026-05-23).
 *
 * Five buckets driven by composite score (0-100 scale) and gate states:
 *   Commit:     score >= 80        color #16A34A  green
 *   Upside:     65 <= score < 80   color #16A34A  green
 *   Best Case:  50 <= score < 65   color #EAB308  yellow
 *   Pipeline:   30 <= score < 50   color #EA580C  orange
 *   Omit:       score < 30         color #DC2626  red
 *
 * Override rules (from CANON.md §Hard Stop and Soft Stop Behavior):
 *   - Hard stop on any CP -> Omit (overrides score)
 *   - Soft stop on any CP -> caps at Upside (a 92-scored deal with a soft stop
 *     reports as Upside, not Commit)
 *   - Critical gate-negative (any CP1-CP4 gate answered negative without a
 *     logged recovery path) -> Omit
 *
 * Score alone drives category. No rep self-declaration.
 *
 * Legacy PUSH / LOSS removed. Both map to "Omit" at read-time throughout.
 *
 * TIMING LADDER NOTE (open question for Bryan's call):
 *   The old timing-eligibility ladder (timing >= 90 for Commit, etc.) was
 *   entirely rep-declared. CANON.md §Locked 2026-05-23 says "score alone
 *   drives category" — no rep self-declaration. The timing-based eligibility
 *   gates in the old `eligibleCategories(timing)` function are now advisory /
 *   display-only (used by DeclaredTiming badge). The `resolveCategory` now
 *   takes compositePct + gate states, not timing. See TODO comment in
 *   `eligibleCategories` below.
 */

export type DeclaredTiming = 90 | 80 | 70 | 60 | 50 | 0; // 0 = "below 50"

/**
 * Canon 5 forecast categories — locked CANON.md lines 167-186.
 * "Omit" replaces legacy "PUSH" and "LOSS" (both mapped here on read).
 */
export type ForecastCategory = "Commit" | "Upside" | "Best Case" | "Pipeline" | "Omit";

// ── Canon colors (CANON.md §Exact Score Bands per Category) ──────────────────

/**
 * Returns the canon hex color for a forecast category.
 * 5-band palette only — NO navy/blue/slate/gray.
 */
export function categoryColor(c: ForecastCategory): string {
  switch (c) {
    case "Commit":     return "#16A34A"; // green
    case "Upside":     return "#16A34A"; // green
    case "Best Case":  return "#EAB308"; // yellow
    case "Pipeline":   return "#EA580C"; // orange
    case "Omit":       return "#DC2626"; // red
  }
}

// ── Score-band mapping (pure, no side effects) ────────────────────────────────

/**
 * Map composite score (0-100) to the score-only forecast category.
 * Does NOT apply hard/soft stop overrides — use resolveCategory() for the
 * authoritative bucket.
 */
export function scoreBandCategory(compositePct: number): ForecastCategory {
  if (compositePct >= 80) return "Commit";
  if (compositePct >= 65) return "Upside";
  if (compositePct >= 50) return "Best Case";
  if (compositePct >= 30) return "Pipeline";
  return "Omit";
}

// ── Authoritative resolver ────────────────────────────────────────────────────

/**
 * Resolve the deal's forecast category per CANON.md §Hard Stop / Soft Stop.
 *
 * @param compositePct        Normalized composite score 0-100
 * @param hasHardStop         Any CP has a hard stop active
 * @param hasSoftStop         Any CP has a soft stop active
 * @param hasCriticalGateNeg  Any CP1-CP4 gate answered negative without
 *                            a logged recovery path
 *
 * Override precedence (first match wins):
 *   1. hardStop OR criticalGateNegative  → "Omit"
 *   2. softStop                          → cap at "Upside"
 *   3. Score bands as above
 */
export function resolveCategory(
  compositePct: number,
  hasHardStop: boolean = false,
  hasSoftStop: boolean = false,
  hasCriticalGateNeg: boolean = false,
): ForecastCategory {
  // Override 1: hard stop or critical gate-negative → always Omit
  if (hasHardStop || hasCriticalGateNeg) return "Omit";

  const scoreBased = scoreBandCategory(compositePct);

  // Override 2: soft stop caps at Upside regardless of score
  if (hasSoftStop) {
    if (scoreBased === "Commit") return "Upside";
    // If score already puts the deal at Upside or below, soft stop has no
    // additional effect (it can't promote a deal).
    return scoreBased;
  }

  return scoreBased;
}

// ── Timing-ladder helpers (advisory / display — NOT the category driver) ──────

/**
 * Categories the deal is *eligible* for given its declared timing %.
 *
 * OPEN QUESTION FOR BRYAN (2026-05-27):
 *   CANON.md §Locked 2026-05-23 says "score alone drives category". The old
 *   timing-eligibility ladder capped deals by rep-declared timing %. Under the
 *   new canon the timing ladder is advisory, not authoritative.
 *
 *   This function is preserved for the TimingBadge and blendedForecastPct
 *   display logic — it is NOT used to assign forecast categories. If Bryan
 *   confirms the timing floor is deprecated entirely, delete this function.
 *
 *   Until confirmed, `resolveCategory()` is the single source of truth.
 *
 * NOTE: Upside did not exist in the old ladder — deals with timing 80/70
 * returned "Best Case" max. Under canon, a 92-scored deal with soft stop
 * returns "Upside" (handled by resolveCategory). The timing ladder below
 * does NOT surface Upside because timing alone cannot produce Upside — that
 * requires a score in the 65-79 band or a soft-stop cap.
 */
export function eligibleCategories(timing: DeclaredTiming): ForecastCategory[] {
  // TODO (Bryan call): confirm whether timing still floors categories under
  // the new score-only canon. For now return score-only eligibility with the
  // timing ladder as a soft constraint.
  if (timing >= 90) return ["Commit", "Upside", "Best Case", "Pipeline", "Omit"];
  if (timing >= 70) return ["Upside", "Best Case", "Pipeline", "Omit"];
  if (timing >= 50) return ["Best Case", "Pipeline", "Omit"];
  // below 50: deal exits active forecast → Omit
  return ["Omit"];
}

// ── Timing helpers (display only) ─────────────────────────────────────────────

export function timingBandLabel(timing: DeclaredTiming): string {
  if (timing >= 90) return "90+";
  if (timing === 80) return "80";
  if (timing === 70) return "70";
  if (timing === 60) return "60";
  if (timing === 50) return "50";
  return "<50";
}

/**
 * Visual color for the declared_timing badge. 90+/80 read confident, 70/60
 * read cautionary, 50/<50 read warning. Stays within canon palette.
 */
export function timingColor(timing: DeclaredTiming): string {
  if (timing >= 80) return "#16A34A"; // green (confident)
  if (timing >= 60) return "#EAB308"; // yellow (cautionary)
  return "#DC2626";                   // red (<50 / warning)
}

// ── Blended forecast % ────────────────────────────────────────────────────────

/**
 * Composite × Timing = blended forecast %, capped at 100.
 * If timing is 0 ("below 50"), the deal is effectively Omit — return 0.
 */
export function blendedForecastPct(compositePct: number, timing: DeclaredTiming): number {
  if (timing === 0) return 0;
  const t = timing / 100;
  return Math.max(0, Math.min(100, Math.round(compositePct * t)));
}

/**
 * Pipeline+flag — 50% timing bucket displays Pipeline with a manager flag.
 */
export function pipelineNeedsAttention(timing: DeclaredTiming): boolean {
  return timing === 50;
}

// ── Distribution helper ───────────────────────────────────────────────────────

export interface DealForecastInput {
  /**
   * Composite score 0-100. Used by resolveCategory.
   * Legacy field `timing` preserved for backwards-compat with callers that
   * have not yet migrated to the gate-state signature.
   */
  compositePct: number;
  /** @deprecated Use hasHardStop/hasSoftStop/hasCriticalGateNeg instead. */
  timing: DeclaredTiming;
  /** Hard stop active on any CP */
  hasHardStop?: boolean;
  /** Soft stop active on any CP */
  hasSoftStop?: boolean;
  /** Critical gate-negative (CP1-CP4) */
  hasCriticalGateNeg?: boolean;
  /** @deprecated Legacy declared category — ignored under canon score-only rule */
  declared?: ForecastCategory;
}

/**
 * Distribution helper for the Executive aggregate widget.
 * Returns count by canon category for an array of deal inputs.
 * All 5 buckets always present in the output (zero-filled).
 */
export function distribution(
  deals: readonly DealForecastInput[],
): Record<ForecastCategory, number> {
  const out: Record<ForecastCategory, number> = {
    Commit: 0, Upside: 0, "Best Case": 0, Pipeline: 0, Omit: 0,
  };
  for (const d of deals) {
    const cat = resolveCategory(
      d.compositePct,
      d.hasHardStop ?? false,
      d.hasSoftStop ?? false,
      d.hasCriticalGateNeg ?? false,
    );
    out[cat]++;
  }
  return out;
}

// ── Average timing band (display only) ───────────────────────────────────────

/**
 * Average timing band, expressed as the rounded average of the numeric
 * declared values (treating "<50" as 40 for the average). Used on the
 * executive dashboard for display purposes only.
 */
export function averageTimingBand(timings: readonly DeclaredTiming[]): number {
  if (!timings.length) return 0;
  let sum = 0;
  for (const t of timings) sum += t === 0 ? 40 : t;
  return Math.round(sum / timings.length);
}
